// A blooming thread's Cards view (docs/blooming.md, "Card previews"): every command of the previewed draft with the
// chat cards it makes, drawn by the real card renderer (AppCard.tsx) in light and dark. Forms are the real private
// cards; result cards come from dry runs on the preview's data (rolled back by the room: nothing is written); data
// cards draw from the same data the full view reads. Each command says which data it shows and what of that record
// isn't on the card, and is marked when it changed from the live version (compare them side by side). "Change this
// card" puts a prompt for the build agent in the thread's composer: tag Bloom to propose changes.
import { useEffect, useMemo, useState } from "react";
import { cardShape, THEME_IDS, themeInfo, type ThemeId, type AppManifest, type AppRecord, type BloomingThread, type Card, type CardPreviewSet, type CardPreviews, type CommandCardPreview, type FieldSpec } from "@bloom/shared";
import { AppData, type AppDataSource } from "../apps/data.ts";
import type { Room } from "../realtime.ts";
import { MessageCard, PrivateCardView, type CardHost } from "./AppCard.tsx";
import { Avatar } from "./ui.tsx";
import "./apps.css";
import "./blooming.css";

type Themes = "both" | "light" | "dark";
const THEMES_KEY = "bloom:card-themes";
const readThemes = (): Themes => {
  try {
    const v = localStorage.getItem(THEMES_KEY);
    return v === "light" || v === "dark" || v === "both" ? v : "both";
  } catch {
    return "both";
  }
};

const PREVIEW_ONLY = "This is a preview: nothing runs here. Try it in the app or in the chat.";

interface Props {
  thread: BloomingThread;
  room: Room;
  host: CardHost;
  me: { id: string; name: string };
  /** Is the Cards view on screen (it loads then, and again when the draft changes). */
  active: boolean;
  /** Puts a prompt for the build agent in the thread's composer. */
  onPrompt: (text: string) => void;
  /** Shows the draft's full view (at a record). */
  onShowInApp: (route: Record<string, string>) => void;
}

export function CardPreviewsPanel({ thread, room, host, me, active, onPrompt, onShowInApp }: Props) {
  const [previews, setPreviews] = useState<CardPreviews | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [themes, setThemesState] = useState<Themes>(readThemes);
  const [compare, setCompare] = useState(false);
  const [tick, setTick] = useState(0);
  const setThemes = (t: Themes) => {
    setThemesState(t);
    try {
      localStorage.setItem(THEMES_KEY, t);
    } catch {
      // a private window: it's only remembered for now
    }
  };
  const preview = thread.preview;
  const key = `${thread.id}:${preview?.version ?? 0}:${preview?.app.bloom?.bundle ?? ""}:${thread.liveVersion ?? ""}:${tick}`;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!active || !preview || key === loadedKey) return;
    let gone = false;
    setLoading(true);
    setError(null);
    void room.bloomCards(thread.id).then((r) => {
      if (gone) return;
      setLoading(false);
      setLoadedKey(key);
      if (r.ok) setPreviews(r.previews);
      else setError(r.message);
    });
    return () => {
      gone = true;
    };
  }, [active, key]);

  if (!preview) {
    return (
      <div className="bloom-empty cardprev-empty">
        <div className="bloom-empty-icon" aria-hidden="true">🃏</div>
        <h2>Chat cards show up here</h2>
        <p className="muted">Once there's a draft, every command's card appears here: what people see in chat when they use it.</p>
      </div>
    );
  }

  const draft = previews?.draft ?? null;
  const live = previews?.live ?? null;
  // Light and Dark (what generated apps design for); comparing with live shows your own theme, whichever it is.
  const shown: ThemeId[] = compare ? [themes === "both" ? currentTheme() : themes] : themes === "both" ? ["light", "dark"] : [themes];
  const liveByName = new Map(live?.commands.map((c) => [c.command, c]) ?? []);
  const removed = live && draft ? live.commands.filter((c) => !draft.commands.some((d) => d.command === c.command)) : [];

  return (
    <div className="cardprev-panel" aria-busy={loading}>
      <div className="cardprev-bar">
        <p className="cardprev-intro muted">
          What each command posts in chat, drawn from the preview's data. Trying them here writes nothing.
        </p>
        <div className="cardprev-controls">
          <div className="cardprev-seg" role="radiogroup" aria-label="Theme">
            {(["light", "dark", "both"] as const).map((t) => (
              <button key={t} type="button" role="radio" aria-checked={themes === t} onClick={() => setThemes(t)}>
                {t === "both" ? "Both" : t === "light" ? "Light" : "Dark"}
              </button>
            ))}
          </div>
          {live && (
            <button type="button" className={`btn btn-small${compare ? " active" : ""}`} aria-pressed={compare} onClick={() => setCompare((c) => !c)}>
              Compare with live v{live.version}
            </button>
          )}
          <button type="button" className="btn btn-small" onClick={() => setTick((n) => n + 1)} disabled={loading} title="Try every command again on the preview's data as it is now">
            {loading ? "Trying…" : "Refresh"}
          </button>
        </div>
      </div>
      {error && <div className="bloom-note error" role="alert"><span>{error}</span></div>}
      {!draft && loading && (
        <div className="cardprev-loading" role="status">
          <span className="bloom-spinner" aria-hidden="true" />
          <span className="shimmer-text">Trying each command on the preview's data…</span>
        </div>
      )}
      {draft && (
        <div className="cardprev-list">
          {draft.version !== preview.version && <p className="muted cardprev-stale">These are draft {draft.version}'s cards. Refresh to see draft {preview.version}'s.</p>}
          {draft.commands.length === 0 && <p className="muted cardprev-stale">This draft has no commands, so it posts nothing in chat.</p>}
          {draft.commands.map((c) => {
            const was = liveByName.get(c.command);
            const status = !live ? null : !was ? "new" : cardShape(c, draft.manifest) !== cardShape(was, live.manifest) ? "changed" : null;
            return (
              <CommandPreview
                key={`${draft.version}:${c.command}`}
                p={c}
                set={draft}
                live={compare && live ? { p: was ?? null, set: live } : null}
                status={status}
                liveVersion={live?.version ?? null}
                themes={shown}
                host={host}
                me={me}
                onPrompt={onPrompt}
                onShowInApp={onShowInApp}
              />
            );
          })}
          {removed.length > 0 && (
            <section className="cardprev-removed" aria-label="Not in this draft">
              <h3>Not in this draft</h3>
              <p className="muted">Live v{live!.version} has {removed.length === 1 ? "this command" : "these commands"}; publishing this draft takes {removed.length === 1 ? "it" : "them"} away.</p>
              <ul>{removed.map((c) => <li key={c.command}><code>{c.label}</code> <span className="muted">{c.description}</span></li>)}</ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function currentTheme(): ThemeId {
  const p = document.documentElement.getAttribute("data-palette") as ThemeId | null;
  if (p && THEME_IDS.includes(p)) return p;
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

const KIND_LABEL: Record<CommandCardPreview["kind"], string> = { result: "Result card", action: "Form", data: "Data card" };

function CommandPreview({ p, set, live, status, liveVersion, themes, host, me, onPrompt, onShowInApp }: {
  p: CommandCardPreview;
  set: CardPreviewSet;
  live: { p: CommandCardPreview | null; set: CardPreviewSet } | null;
  status: "new" | "changed" | null;
  liveVersion: number | null;
  themes: ThemeId[];
  host: CardHost;
  me: { id: string; name: string };
  onPrompt: (text: string) => void;
  onShowInApp: (route: Record<string, string>) => void;
}) {
  const draftHost = usePreviewHost(set, p, host, onShowInApp);
  const liveHost = usePreviewHost(live?.set ?? set, live?.p ?? p, host, onShowInApp);
  const facts = useMemo(() => describe(p, set, host.names), [p, set, host.names]);
  const bound = p.cards?.find((c) => c.bind)?.bind;
  const boundExists = !!bound && !!set.records[bound.collection]?.some((r) => r.id === bound.id);
  const name = `the ${p.label} card`;
  const columns = live ? [{ label: `Draft ${set.version}`, p, host: draftHost }, { label: `Live v${live.set.version}`, p: live.p, host: liveHost }] : [{ label: null, p, host: draftHost }];

  const stage = (title: string, pick: (q: CommandCardPreview) => React.ReactNode | null) => {
    const any = columns.some((c) => c.p && pick(c.p));
    if (!any) return null;
    return (
      <div className="cardprev-stage">
        <div className="cardprev-stage-title">{title}</div>
        <div className={`cardprev-frames n${columns.length * themes.length}`}>
          {columns.flatMap((col) =>
            themes.map((t) => (
              <div key={`${col.label}:${t}`} className={`cardprev-frame theme-${t}`} data-frame-theme={t} data-frame-version={col.label ?? "draft"}>
                {(col.label || themes.length > 1) && <div className="cardprev-frame-label">{[col.label, themes.length > 1 ? themeInfo(t).label : null].filter(Boolean).join(" · ")}</div>}
                {col.p ? (pick(col.p) ?? <p className="muted cardprev-none">Nothing here in this version.</p>) : <p className="muted cardprev-none">Not in the live version.</p>}
              </div>
            )),
          )}
        </div>
      </div>
    );
  };
  // A column's own host draws its cards (each version reads its own data).
  const hostFor = (q: CommandCardPreview) => (live && q === live.p ? liveHost : draftHost);
  const posted = (q: CommandCardPreview) =>
    q.cards?.length ? (
      <div className="cardprev-posts">
        {q.cards.map((c, i) => (
          <Posted key={i} card={c} host={hostFor(q)} me={me} />
        ))}
      </div>
    ) : null;
  const data = (q: CommandCardPreview) => (q.data ? <Posted card={q.data} host={hostFor(q)} me={me} /> : null);
  const form = (q: CommandCardPreview) =>
    q.form ? (
      <PrivateCardView id={`preview-${q.command}`} card={q.form} host={hostFor(q)} onSubmit={() => Promise.resolve({ ok: false, message: PREVIEW_ONLY })} onDismiss={() => {}} />
    ) : null;
  const done = (q: CommandCardPreview) =>
    q.done && q.form ? (
      <PrivateCardView id={`preview-done-${q.command}`} card={{ ...q.form, done: q.done }} host={hostFor(q)} onSubmit={() => Promise.resolve({ ok: false, message: PREVIEW_ONLY })} onDismiss={() => {}} />
    ) : null;

  return (
    <article className="cardprev" data-command={p.command} aria-labelledby={`cardprev-${p.command}`}>
      <header className="cardprev-head">
        <div className="cardprev-title">
          <code id={`cardprev-${p.command}`}>{p.label}</code>
          <span className="bloom-tag">{KIND_LABEL[p.kind]}</span>
          {status === "changed" && <span className="bloom-tag live" data-status="changed">Changed from live v{liveVersion}</span>}
          {status === "new" && <span className="bloom-tag live" data-status="new">New</span>}
        </div>
        <p className="cardprev-desc muted">{p.description}</p>
      </header>
      {facts.length > 0 && (
        <ul className="cardprev-facts">
          {facts.map((f) => <li key={f.label} className={f.warn ? "warn" : undefined}><span className="cardprev-fact-label">{f.label}</span> {f.text}</li>)}
        </ul>
      )}
      {p.kind === "data" && stage("Posted in chat", data)}
      {p.kind === "action" && stage("The form (only the person who typed it sees it)", form)}
      {p.kind === "action" && stage("Once it's sent, posts", posted)}
      {p.kind === "result" && stage("Posted in chat", posted)}
      {p.kind === "result" && stage("The form, when something's missing (only they see it)", form)}
      {stage("Then the form says", done)}
      {p.note && <p className="cardprev-note muted" role="note">{p.note}</p>}
      <footer className="cardprev-foot">
        <button type="button" className="btn btn-small btn-primary" onClick={() => onPrompt(`@Bloom In ${name}, `)} data-change-card={p.command}>
          Change this card
        </button>
        {facts.find((f) => f.missing)?.missing && (
          <button type="button" className="btn btn-small" onClick={() => onPrompt(`@Bloom In ${name}, also show the ${facts.find((f) => f.missing)!.missing!.join(", ")}.`)}>
            Show {facts.find((f) => f.missing)!.missing!.join(", ")} on it
          </button>
        )}
        {status === "changed" && (
          <button type="button" className="btn btn-small" onClick={() => onPrompt(`@Bloom Make ${name} look like it does in the live version again, `)}>
            Undo the change
          </button>
        )}
        <button type="button" className="link-btn cardprev-open" onClick={() => onShowInApp(boundExists ? { record: bound!.id } : {})}>
          See it in the app
        </button>
      </footer>
    </article>
  );
}

/** A card as a message in chat: who posted it, then the card (its height reserved like in the list). */
function Posted({ card, host, me }: { card: Card; host: CardHost; me: { id: string; name: string } }) {
  return (
    <div className="cardprev-msg">
      <Avatar id={me.id} name={me.name} size={32} />
      <div className="cardprev-msg-body">
        <div className="cardprev-who"><strong>{me.name}</strong> <span className="muted">Today</span></div>
        <MessageCard card={card} host={host} />
      </div>
    </div>
  );
}

/**
 * The host cards draw with in a preview: the version's data (as tried: with the dry run's writes), its manifest, no
 * calls (buttons and forms say it's a preview), Open showing the draft's full view.
 */
function usePreviewHost(set: CardPreviewSet, p: CommandCardPreview, host: CardHost, onShowInApp: (route: Record<string, string>) => void): CardHost {
  return useMemo(() => {
    const data = new AppData();
    for (const [c, rows] of Object.entries(set.records)) {
      const byId = new Map(rows.map((r) => [r.id, r]));
      for (const w of p.writes ?? []) {
        if (w.collection !== c) continue;
        if (w.record) byId.set(w.id, w.record);
        else byId.delete(w.id);
      }
      data.setAll(set.app, c, [...byId.values()]);
    }
    const source: AppDataSource = { data, watchCollection: () => () => {} };
    return {
      ...host,
      source,
      apps: [set.manifest, ...host.apps.filter((a) => a.slug !== set.app)],
      call: () => Promise.resolve({ ok: false as const, message: PREVIEW_ONLY }),
      open: (_app, route) => onShowInApp(route ?? {}),
      preview: true,
    };
  }, [set, p, host]);
}

interface Fact {
  label: string;
  text: string;
  warn?: boolean;
  /** Fields of the record that aren't on the card (for "Show … on it"). */
  missing?: string[];
}

/** Which data the command's cards show, and what of it they leave out (where chat cards drift from the full view). */
function describe(p: CommandCardPreview, set: CardPreviewSet, names: Map<string, string>): Fact[] {
  const m = set.manifest;
  const out: Fact[] = [];
  const fieldName = (k: string, f?: FieldSpec) => (f?.description && f.description.length <= 24 ? f.description.toLowerCase() : k);
  if (p.data && p.view) {
    const v = m.views?.[p.view];
    const coll = v ? m.collections[v.collection] : undefined;
    if (v && coll) {
      const shows = [v.titleField, ...(v.metaFields ?? []), ...(v.countField ? [v.countField] : [])];
      const order = v.sortBy ? `by ${v.sortBy}` : "newest first";
      const where = v.where ? ` where ${Object.entries(v.where).map(([k, vals]) => `${k} is ${vals.join(" or ")}`).join(", ")}` : "";
      out.push({ label: "Shows", text: `the ${v.collection} collection (view “${p.view}”): ${v.limit ?? 5} ${order}${where}; ${shows.join(", ")}` });
      const left = Object.entries(coll.fields).filter(([k, f]) => !shows.includes(k) && f.type !== "media").map(([k]) => k);
      if (left.length) out.push({ label: "Not on the card", text: left.join(", "), warn: true });
    }
    return out;
  }
  const card = p.cards?.find((c) => c.bind);
  if (card?.bind) {
    const coll = m.collections[card.bind.collection];
    const rec: AppRecord | undefined = p.writes?.find((w) => w.id === card.bind!.id && w.record)?.record ?? set.records[card.bind.collection]?.find((r) => r.id === card.bind!.id);
    out.push({ label: "Shows", text: `a record in ${card.bind.collection}, live (buttons and counts follow it)` });
    if (coll && rec) {
      const words = [card.title, card.text?.replace(/<@([^>]+)>/g, (_, id: string) => names.get(id) ?? ""), ...(card.meta ?? []), ...(card.buttons?.map((b) => b.label) ?? [])].filter(Boolean).join(" ").toLowerCase();
      const toggled = new Set(card.buttons?.map((b) => b.toggleField).filter(Boolean) as string[]);
      const missing: string[] = [];
      for (const [k, f] of Object.entries(coll.fields)) {
        const v = rec.data[k];
        if (v === undefined || v === null || v === "" || toggled.has(k) || (f.type === "media" && card.media === k)) continue;
        if (f.type === "media" || f.type === "datetime" || f.type === "record" || f.type === "members" || f.type === "channel") {
          if (f.type === "media" && Array.isArray(v) && v.length) missing.push(fieldName(k, f));
          continue;
        }
        const s = (f.type === "member" ? (names.get(String(v)) ?? "") : String(v)).toLowerCase();
        if (s && !words.includes(s)) missing.push(fieldName(k, f));
      }
      if (missing.length) out.push({ label: "Not on the card", text: `${missing.join(", ")} (the record has ${missing.length === 1 ? "it" : "them"}; the app may show ${missing.length === 1 ? "it" : "them"})`, warn: true, missing });
    }
  } else if (p.cards?.length) {
    out.push({ label: "Shows", text: "a fixed line: nothing on it changes after it's posted" });
  }
  if (p.input && Object.keys(p.input).length) out.push({ label: "Tried with", text: describeInput(p.input, m, p.action, set, names) });
  if (p.as) out.push({ label: "Tried as", text: `an ${p.as} (only ${m.actions[p.action!]?.roles?.join(", ")}s can run it)` });
  return out;
}

function describeInput(input: Record<string, unknown>, m: AppManifest, action: string | undefined, set: CardPreviewSet, names: Map<string, string>): string {
  const specs = action ? (m.actions[action]?.inputs ?? []) : [];
  return Object.entries(input)
    .map(([k, v]) => {
      const s = specs.find((i) => i.key === k);
      let shown = String(v);
      if (s?.type === "member") shown = names.get(shown) ?? "someone";
      else if (s?.type === "record" && s.collection) {
        const r = set.records[s.collection]?.find((x) => x.id === v);
        const title = r && Object.values(r.data).find((x) => typeof x === "string");
        shown = title ? `“${title}”` : "a record";
      } else if (s?.type === "datetime") shown = new Date(Number(v)).toLocaleString();
      else if (typeof v === "string") shown = `“${v}”`;
      return `${s?.label ?? k}: ${shown}`;
    })
    .join(" · ");
}
